import { hoursToMinutes } from '@acme/common'
import { Row, SortingFn, sortingFns } from '@tanstack/react-table'
import { GridColDefPro, ValidRowModel } from './types'

const DATA_BR = /^(\d{2})\/(\d{2})\/(\d{4})$/
const HORAS = /^-?\d+:\d{2}$/
const NUMERO_NO_INICIO = /^(-?\d+(?:[.,]\d+)?)(?:\s|$)/

/** O `sortingFn` do TanStack só lê o valor da célula, não a linha inteira. */
function linhaComValor(valor: unknown) {
    return { getValue: () => valor } as unknown as Row<any>
}

function isVazio(valor: unknown) {
    return valor === '' || valor === null || valor === undefined
}

/**
 * Converte o valor exibido no que deve ser comparado: horas `HH:mm` e
 * "N dia(s)" viram número, e `DD/MM/YYYY` vira `YYYY-MM-DD` (como texto, o dia
 * viria antes do mês e do ano).
 */
function valorOrdenavel(valor: unknown) {
    if (typeof valor !== 'string') return valor
    if (HORAS.test(valor)) {
        const sinal = valor.startsWith('-') ? -1 : 1
        return sinal * hoursToMinutes(valor.replace('-', ''))
    }
    const numero = NUMERO_NO_INICIO.exec(valor)
    if (numero) return Number(numero[1].replace(',', '.'))
    const data = DATA_BR.exec(valor)
    return data ? `${data[3]}-${data[2]}-${data[1]}` : valor
}

/** Compara dois valores de célula como o grid ordena; vazio vem primeiro. */
export function compararValores(a: unknown, b: unknown) {
    const valorA = valorOrdenavel(a)
    const valorB = valorOrdenavel(b)
    if (isVazio(valorA) || isVazio(valorB))
        return Number(!isVazio(valorA)) - Number(!isVazio(valorB))
    if (typeof valorA === 'number' && typeof valorB === 'number')
        return valorA - valorB
    if (valorA instanceof Date && valorB instanceof Date)
        return valorA.getTime() - valorB.getTime()
    return sortingFns.alphanumeric(
        linhaComValor(valorA),
        linhaComValor(valorB),
        '',
    )
}

/** Valor de uma coluna de agrupamento: o rótulo declarado ou o próprio valor. */
export function valorDoAgrupamento(
    coluna: GridColDefPro | undefined,
    linha: ValidRowModel,
    valor: unknown,
) {
    return coluna?.rotuloDoGrupo ? coluna.rotuloDoGrupo(linha) : valor
}

function folhasDoGrupo(row: Row<any>) {
    return row.getLeafRows().filter(folha => !folha.getIsGrouped())
}

/**
 * Cria o `sortingFn` de grid agrupado. Na linha de grupo o TanStack só tem
 * valor para coluna agrupada ou número somado; para o resto o grupo compara
 * pela soma das folhas, se a coluna for numérica (horas, "N dia(s)"), ou pelo
 * próprio rótulo, se for texto.
 */
export function criarOrdenacaoComGrupos(
    colunas: GridColDefPro[],
): SortingFn<any> {
    const rotuloDoGrupo = (row: Row<any>) =>
        valorDoAgrupamento(
            colunas.find(coluna => coluna.field === row.groupingColumnId),
            folhasDoGrupo(row)[0]?.original ?? row.original,
            row.groupingValue,
        )

    const valorDoGrupo = (row: Row<any>, columnId: string) => {
        if (columnId === row.groupingColumnId) return rotuloDoGrupo(row)

        const agregado = row.getValue(columnId)
        if (agregado !== undefined) return agregado

        const valores = folhasDoGrupo(row)
            .map(folha => valorOrdenavel(folha.getValue(columnId)))
            .filter(valor => !isVazio(valor))
        if (valores.every(valor => typeof valor === 'number'))
            return (valores as number[]).reduce((total, n) => total + n, 0)

        return rotuloDoGrupo(row)
    }

    const valor = (row: Row<any>, columnId: string) =>
        row.getIsGrouped()
            ? valorDoGrupo(row, columnId)
            : row.getValue(columnId)

    return (a, b, columnId) =>
        compararValores(valor(a, columnId), valor(b, columnId))
}
